import { invokeFunction } from '../api'
import { toApiRequestError } from '../errors'

export type PolicyCategory =
  | 'governance'
  | 'patient_rights'
  | 'infection_control'
  | 'medication_safety'
  | 'life_safety'
  | 'environment_of_care'
  | 'emergency_preparedness'
  | 'medical_records'
  | 'staffing'
  | 'quality_assessment'

export type PolicyStatus = 'draft' | 'in_review' | 'approved' | 'retired'

export interface PolicyRecord {
  policy_id: string
  tenant_id: string
  facility_id: string
  title: string
  category: PolicyCategory
  cms_cfc_sections: string[]
  version_number: number
  status: PolicyStatus
  summary: string | null
  content_text: string | null
  effective_date: string | null
  review_date: string | null
  approved_by: string | null
  approved_at: string | null
  created_by: string | null
  created_at: string
  updated_at: string
}

export interface PolicyAcknowledgmentSummary {
  total_acknowledgments: number
  self_acknowledged: boolean
  self_acknowledged_at: string | null
}

export interface PolicyAcknowledgmentCounts {
  policy_id: string
  assigned_user_count: number
  acknowledged_user_count: number
}

export interface PolicyAcknowledgmentReceipt {
  acknowledgment_id: string
  acknowledged_at: string
}

export interface PolicyInput {
  facility_id: string
  title: string
  category: PolicyCategory
  summary?: string | null
  content_text?: string | null
  status?: PolicyStatus
  effective_date?: string | null
  review_date?: string | null
}

export interface PolicyFilters {
  facilityId?: string
  category?: PolicyCategory
  status?: PolicyStatus
}

function asRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function parsePolicy(value: unknown): PolicyRecord {
  if (!asRecord(value)) throw new Error('Malformed policy response from server')
  if (typeof value.policy_id !== 'string') throw new Error('Malformed policy response from server')
  if (typeof value.tenant_id !== 'string') throw new Error('Malformed policy response from server')
  if (typeof value.facility_id !== 'string') throw new Error('Malformed policy response from server')
  if (typeof value.title !== 'string') throw new Error('Malformed policy response from server')
  if (typeof value.category !== 'string') throw new Error('Malformed policy response from server')
  if (typeof value.version_number !== 'number') throw new Error('Malformed policy response from server')
  if (typeof value.status !== 'string') throw new Error('Malformed policy response from server')
  if (typeof value.created_at !== 'string') throw new Error('Malformed policy response from server')
  if (typeof value.updated_at !== 'string') throw new Error('Malformed policy response from server')

  return {
    policy_id: value.policy_id,
    tenant_id: value.tenant_id,
    facility_id: value.facility_id,
    title: value.title,
    category: value.category as PolicyCategory,
    cms_cfc_sections: Array.isArray(value.cms_cfc_sections)
      ? value.cms_cfc_sections.filter((entry): entry is string => typeof entry === 'string')
      : [],
    version_number: value.version_number,
    status: value.status as PolicyStatus,
    summary: typeof value.summary === 'string' ? value.summary : null,
    content_text: typeof value.content_text === 'string' ? value.content_text : null,
    effective_date: typeof value.effective_date === 'string' ? value.effective_date : null,
    review_date: typeof value.review_date === 'string' ? value.review_date : null,
    approved_by: typeof value.approved_by === 'string' ? value.approved_by : null,
    approved_at: typeof value.approved_at === 'string' ? value.approved_at : null,
    created_by: typeof value.created_by === 'string' ? value.created_by : null,
    created_at: value.created_at,
    updated_at: value.updated_at,
  }
}

function buildQuery(filters?: PolicyFilters): string {
  const params = new URLSearchParams()
  if (filters?.facilityId) params.set('facility_id', filters.facilityId)
  if (filters?.category) params.set('category', filters.category)
  if (filters?.status) params.set('status', filters.status)
  const query = params.toString()
  return query.length > 0 ? `?${query}` : ''
}

export async function getPolicies(filters?: PolicyFilters): Promise<PolicyRecord[]> {
  const { data, error } = await invokeFunction<{ policies: unknown }>('policies', {
    method: 'GET',
    path: `/${buildQuery(filters)}`,
  })

  if (error) throw toApiRequestError(error, 'Failed to load policies')
  if (!data || !Array.isArray(data.policies)) throw new Error('Malformed policies response from server')
  return data.policies.map(parsePolicy)
}

export async function getPolicy(policyId: string): Promise<{ policy: PolicyRecord; acknowledgmentSummary: PolicyAcknowledgmentSummary }> {
  const { data, error } = await invokeFunction<unknown>('policies', {
    method: 'GET',
    path: `/${policyId}`,
  })

  if (error) throw toApiRequestError(error, 'Failed to load policy detail')
  if (!asRecord(data)) throw new Error('Malformed policy detail response from server')
  if (!asRecord(data.acknowledgment_summary)) throw new Error('Malformed policy detail response from server')

  return {
    policy: parsePolicy(data.policy),
    acknowledgmentSummary: {
      total_acknowledgments:
        typeof data.acknowledgment_summary.total_acknowledgments === 'number'
          ? data.acknowledgment_summary.total_acknowledgments
          : 0,
      self_acknowledged: data.acknowledgment_summary.self_acknowledged === true,
      self_acknowledged_at:
        typeof data.acknowledgment_summary.self_acknowledged_at === 'string'
          ? data.acknowledgment_summary.self_acknowledged_at
          : null,
    },
  }
}

export async function getPolicyAcknowledgmentSummary(policyId: string): Promise<PolicyAcknowledgmentCounts> {
  const { data, error } = await invokeFunction<unknown>('policies', {
    method: 'GET',
    path: `/${policyId}/acknowledgments`,
  })

  if (error) throw toApiRequestError(error, 'Failed to load policy acknowledgment summary')
  if (!asRecord(data)) throw new Error('Malformed policy acknowledgment summary from server')
  if (typeof data.policy_id !== 'string') throw new Error('Malformed policy acknowledgment summary from server')

  return {
    policy_id: data.policy_id,
    assigned_user_count: typeof data.assigned_user_count === 'number' ? data.assigned_user_count : 0,
    acknowledged_user_count: typeof data.acknowledged_user_count === 'number' ? data.acknowledged_user_count : 0,
  }
}

export async function acknowledgePolicy(policyId: string): Promise<PolicyAcknowledgmentReceipt> {
  const { data, error } = await invokeFunction<unknown>('policies', {
    method: 'POST',
    path: `/${policyId}/acknowledge`,
  })

  if (error) throw toApiRequestError(error, 'Failed to acknowledge policy')
  if (!asRecord(data) || !asRecord(data.acknowledgment)) throw new Error('Malformed policy acknowledgment response from server')
  if (typeof data.acknowledgment.acknowledgment_id !== 'string') throw new Error('Malformed policy acknowledgment response from server')
  if (typeof data.acknowledgment.acknowledged_at !== 'string') throw new Error('Malformed policy acknowledgment response from server')

  return {
    acknowledgment_id: data.acknowledgment.acknowledgment_id,
    acknowledged_at: data.acknowledgment.acknowledged_at,
  }
}

export async function createPolicy(input: PolicyInput): Promise<PolicyRecord> {
  const { data, error } = await invokeFunction<unknown>('policies', {
    method: 'POST',
    path: '/',
    body: input,
  })

  if (error) throw toApiRequestError(error, 'Failed to create policy')
  if (!asRecord(data)) throw new Error('Malformed policy create response from server')
  return parsePolicy(data.policy)
}

export async function updatePolicy(policyId: string, input: Partial<PolicyInput>): Promise<PolicyRecord> {
  const { data, error } = await invokeFunction<unknown>('policies', {
    method: 'PATCH',
    path: `/${policyId}`,
    body: input,
  })

  if (error) throw toApiRequestError(error, 'Failed to update policy')
  if (!asRecord(data)) throw new Error('Malformed policy update response from server')
  return parsePolicy(data.policy)
}
